"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { authClient } from "@repo/auth/client";
import { useAuthErrorMessages } from "@saas/auth/hooks/errors-messages";
import { useFormErrors } from "@shared/hooks/form-errors";
import { Alert, AlertDescription, AlertTitle } from "@ui/components/alert";
import { Button } from "@ui/components/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@ui/components/form";
import { Input } from "@ui/components/input";
import { AlertTriangleIcon, MailboxIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import { z } from "zod";

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
});

type FormValues = z.infer<typeof formSchema>;

interface InvitationSignupFormProps {
  invitationId: string;
  email: string;
  organizationName: string;
  logoUrl?: string;
}

/**
 * Sign-up form for invited users. It only asks for their name and emails a
 * magic link, so joining proves the invitee controls the invited mailbox. The
 * link creates the account (if needed) and opens the invitation page, where
 * the invitation is accepted.
 */
export function InvitationSignupForm({
  invitationId,
  email,
  organizationName,
  logoUrl,
}: InvitationSignupFormProps) {
  const t = useTranslations();
  const { zodErrorMap } = useFormErrors();
  const { getAuthErrorMessage } = useAuthErrorMessages();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema, {
      errorMap: zodErrorMap,
    }),
    values: {
      name: "",
    },
  });

  const onSubmit: SubmitHandler<FormValues> = async ({ name }) => {
    const { error } = await authClient.signIn.magicLink({
      email,
      name,
      callbackURL: `/app/organization-invitation/${invitationId}`,
    });

    if (error) {
      form.setError("root", {
        message: getAuthErrorMessage(error.code),
      });
    }
  };

  const isSubmitting = form.formState.isSubmitting;
  const isLinkSent = form.formState.isSubmitSuccessful && !form.formState.errors.root;

  return (
    <div>
      {/* Organization Logo */}
      {logoUrl && (
        <div className="mb-6 flex justify-center">
          {logoUrl.startsWith("/image-proxy/") ? (
            // Use regular img tag for image-proxy URLs (which redirect to signed S3 URLs)
            // Next.js Image component doesn't handle redirects well
            // biome-ignore lint/performance/noImgElement: image-proxy URLs require native img tag
            <img
              src={logoUrl}
              alt={`${organizationName} logo`}
              className="max-h-16 w-auto object-contain"
            />
          ) : (
            <Image
              src={logoUrl}
              alt={`${organizationName} logo`}
              width={200}
              height={64}
              className="max-h-16 w-auto object-contain"
            />
          )}
        </div>
      )}

      <h1 className="font-bold text-xl md:text-2xl">{t("auth.invitation.title")}</h1>
      <p className="mt-1 mb-6 text-foreground/60">
        {t("auth.invitation.message", { organizationName })}
      </p>

      {isLinkSent ? (
        <Alert variant="success">
          <MailboxIcon />
          <AlertTitle>{t("auth.login.hints.linkSent.title")}</AlertTitle>
          <AlertDescription>{t("auth.login.hints.linkSent.message")}</AlertDescription>
        </Alert>
      ) : (
        <Form {...form}>
          <form
            className="flex flex-col items-stretch gap-4"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            {form.formState.isSubmitted && form.formState.errors.root && (
              <Alert variant="error">
                <AlertTriangleIcon />
                <AlertDescription>{form.formState.errors.root.message}</AlertDescription>
              </Alert>
            )}

            {/* Name input */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("auth.signup.name")}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={t("auth.invitation.namePlaceholder")} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button loading={isSubmitting} disabled={isSubmitting}>
              {t("auth.invitation.submit")}
            </Button>
          </form>
        </Form>
      )}
    </div>
  );
}
